import { useRef, useState } from "react";
import DataContainer from "../services/DataContainer";
import { ImageStatus } from "./useRenderOnDemand.types";
import JSZip from "jszip";
import { saveAs } from "file-saver";

export interface RenderSpec {
  sequence: string;
  attributes: Record<string, string | undefined>;
  renderVersion: number;
  configuration: any;
  format?: string;
  width: number;
  height: number;
  type: string;
  compressionQuality?: number;
}

export interface DownloadConfigurationImagesParams {
  imageSpecs: RenderSpec[];
  productName: string;
  format: "png" | "jpg";
}

const CHECK_STATUS_MAX_TRIES = 100;

const responseHandler = async (res: any): Promise<string | undefined> => {
  if (res.data.status === ImageStatus.Queued) {
    let tries = 0;
    while (tries < CHECK_STATUS_MAX_TRIES) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      const response = await DataContainer.checkStatus(res.data);
      tries++;
      if (response.data.status === ImageStatus.Ready) {
        return response.data.urlAssets[0];
      }
    }
    return undefined;
  }
  if (res.data.status === ImageStatus.Ready) {
    return res.data.urlAssets[0];
  }
  return undefined;
};

export function useDownloadConfigurationImages() {
  const imagesCacheRef = useRef<{ key: string; images: string[] } | null>(null);  const [isLoadingZip, setIsLoadingZip] = useState(false);
  const [isLoadingJPEGZip, setIsLoadingJPEGZip] = useState(false);

  const downloadImages = async ({
    imageSpecs,
    productName,
    format,
  }: DownloadConfigurationImagesParams) => {
    format === "png" ? setIsLoadingZip(true) : setIsLoadingJPEGZip(true);

    const currentKey = JSON.stringify(imageSpecs);
    let images = imagesCacheRef.current?.key === currentKey ? imagesCacheRef.current.images : [];

    if (images.length === 0) {
      const responses = await Promise.all(
        imageSpecs.map((req) => DataContainer.getRender(req)),
      );
      const getImages: string[] = [];
      for (const res of responses) {
        const url = await responseHandler(res);
        if (url) getImages.push(url);
      }
      imagesCacheRef.current = { key: currentKey, images: getImages };
      images = getImages;
    }

    const zip = new JSZip();

    const addImageToZip = async (url: string, fileName: string, spec: RenderSpec) => {
      if (fileName.endsWith(".jpg")) {
        const response = await fetch(url).then(async (res) => await res.blob());
        const imgUrl = window.URL.createObjectURL(response);
        const canvas = document.createElement("canvas");
        canvas.width = spec.width;
        canvas.height = spec.height;
        const img = document.createElement("img");
        img.crossOrigin = "Anonymous";
        await new Promise((resolve) => {
          img.src = imgUrl;
          img.onload = resolve;
        });
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.fillStyle = "white";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
        }
        const blob = await new Promise<Blob | null>((resolve) =>
          canvas.toBlob(resolve, "image/jpeg", 0.9),
        );
        if (blob) {
          zip.file(fileName, blob);
        }
      } else {
        const response = await fetch(
          `${url}?format=PNG&crop=true&height=${spec.height}&width=${spec.width}&top=${Math.round(spec.height * 0.3)}&left=${Math.round(spec.width * 0.3)}`
        );
        const blob = await response.blob();
        zip.file(fileName, blob);
      }
    };

    const promises: Promise<void>[] = [];
    images.forEach((url, idx) => {
      const fileName = `${productName} ${idx + 1}${
        format === "png" ? ".png" : ".jpg"
      }`;
      promises.push(addImageToZip(url, fileName, imageSpecs[idx]));
    });

    await Promise.allSettled(promises);
    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, productName);

    format === "png" ? setIsLoadingZip(false) : setIsLoadingJPEGZip(false);
  };

  return { downloadImages, isLoadingZip, isLoadingJPEGZip };
}
